import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsCalendar, BsCameraVideo, BsPlus, BsSearch, BsX } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useErrorStore } from '#lib/stores/errorStore';

import { createCalendarEvent, listContacts, listDeals, listCompanies } from '../../../api/crmApi';
import { useCalendarConnections } from '../../../models/calendar/queries';

const VideoCallSelector = ({ hasGoogle, hasMicrosoft, videoCall, onChange }) => (
	<div>
		<label className="text-xs font-medium text-text mb-1.5 block">
			<BsCameraVideo className="inline w-3.5 h-3.5 mr-1" />
			Video-Call
		</label>
		<div className="flex gap-2">
			<button
				type="button"
				onClick={() => onChange(null)}
				className={`text-xs px-3 py-1.5 rounded-control border transition-colors ${!videoCall ? 'bg-bgBase-400 border-bgBase-600 text-text' : 'bg-white border-bgBase-500 text-bgBase-800'}`}
			>
				Keiner
			</button>
			{hasGoogle && (
				<button
					type="button"
					onClick={() => onChange('google_meet')}
					className={`text-xs px-3 py-1.5 rounded-control border transition-colors ${videoCall === 'google_meet' ? 'bg-primary-100 border-primary-300 text-primary-700' : 'bg-white border-bgBase-500 text-bgBase-800'}`}
				>
					Google Meet
				</button>
			)}
			{hasMicrosoft && (
				<button
					type="button"
					onClick={() => onChange('teams')}
					className={`text-xs px-3 py-1.5 rounded-control border transition-colors ${videoCall === 'teams' ? 'bg-primary-100 border-primary-300 text-primary-700' : 'bg-white border-bgBase-500 text-bgBase-800'}`}
				>
					Microsoft Teams
				</button>
			)}
		</div>
	</div>
);

VideoCallSelector.propTypes = {
	hasGoogle: PropTypes.bool.isRequired,
	hasMicrosoft: PropTypes.bool.isRequired,
	videoCall: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};

const DURATION_OPTIONS = [
	{ value: 15, label: '15 Min' },
	{ value: 30, label: '30 Min' },
	{ value: 45, label: '45 Min' },
	{ value: 60, label: '1 Stunde' },
	{ value: 90, label: '1,5 Stunden' },
	{ value: 120, label: '2 Stunden' },
];

/**
 * Debounced company search hook.
 * @param {string} query - Search string (searches after 2+ chars, 250ms debounce)
 * @returns {{ results: object[], loading: boolean }}
 */
function useCompanySearch(query) {
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!query || query.trim().length < 2) {
			setResults([]);
			return;
		}
		let cancelled = false;
		const timer = setTimeout(async () => {
			setLoading(true);
			try {
				const res = await listCompanies({ search: query.trim(), pageSize: 6 });
				if (!cancelled) setResults(res.companies ?? []);
			} catch {
				/* ignore */
			}
			if (!cancelled) setLoading(false);
		}, 250);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query]);

	return { results, loading };
}

/**
 * Modal for scheduling a calendar meeting.
 * When companyId is provided (e.g. from company page), contacts load automatically.
 * When companyId is absent (e.g. from calendar page), a company search field is shown.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} [props.companyId] - Pre-selected company ID
 * @param {string} [props.companyName] - Pre-selected company name
 * @param {string} [props.dealId] - Pre-selected deal ID
 * @param {string} [props.prefillDate] - ISO datestring to pre-fill date/time
 */
const MeetingScheduleModal = ({ isOpen, onClose, companyId, companyName, dealId: preselectedDealId, prefillDate }) => {
	const qc = useQueryClient();
	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);

	// Company search (only when companyId is not provided)
	const showCompanyPicker = !companyId;
	const [companyInput, setCompanyInput] = useState('');
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
	const companyDropdownRef = useRef(null);

	const { results: companyResults, loading: companySearchLoading } = useCompanySearch(
		showCompanyPicker && !selectedCompany ? companyInput : '',
	);

	const resolvedCompanyId = companyId || selectedCompany?.id || '';
	const resolvedCompanyName = companyName || selectedCompany?.companyName || '';

	// Load contacts for resolved company
	const { data: contactsData } = useQuery({
		queryKey: ['crm', 'contacts', resolvedCompanyId],
		queryFn: () => listContacts(resolvedCompanyId),
		enabled: isOpen && Boolean(resolvedCompanyId),
	});
	const contacts = useMemo(() => contactsData?.contacts ?? [], [contactsData]);

	// Load deals for resolved company
	const { data: dealsData } = useQuery({
		queryKey: ['crm', 'deals', 'company', resolvedCompanyId],
		queryFn: () => listDeals({ crmCompanyId: resolvedCompanyId }),
		enabled: isOpen && Boolean(resolvedCompanyId),
	});
	const deals = dealsData?.deals ?? [];

	// Calendar connections
	const { data: connections = [] } = useCalendarConnections();

	// Form state
	const defaultDate = prefillDate
		? new Date(prefillDate).toISOString().slice(0, 10)
		: new Date().toISOString().slice(0, 10);
	const defaultTime = prefillDate
		? new Date(prefillDate).toISOString().slice(11, 16)
		: (() => {
				const d = new Date();
				d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
				return d.toISOString().slice(11, 16);
			})();

	const [title, setTitle] = useState(`Meeting mit ${companyName || ''}`);
	const [date, setDate] = useState(defaultDate);
	const [time, setTime] = useState(defaultTime);
	const [duration, setDuration] = useState(60);
	const [selectedContacts, setSelectedContacts] = useState([]);
	const [emailOverrides, setEmailOverrides] = useState({}); // { contactId: 'manual@email.de' }
	const [manualEmails, setManualEmails] = useState([]); // ['extra@email.de']
	const [manualEmailInput, setManualEmailInput] = useState('');
	const [videoCall, setVideoCall] = useState(null);
	const [description, setDescription] = useState('');
	const [dealId, setDealId] = useState(preselectedDealId || '');
	const [calendarConnectionId, setCalendarConnectionId] = useState('');

	// Default to first connection once loaded
	const effectiveConnectionId = calendarConnectionId || connections[0]?.id || '';

	// Determine available video options based on connected calendars
	const hasGoogle = connections.some((c) => c.provider === 'google_calendar');
	const hasMicrosoft = connections.some((c) => c.provider === 'microsoft_calendar');

	// Company search handlers
	const handleCompanyInputChange = useCallback(
		(e) => {
			setCompanyInput(e.target.value);
			if (selectedCompany) {
				setSelectedCompany(null);
				setSelectedContacts([]);
				setEmailOverrides({});
				setManualEmails([]);
				setDealId('');
			}
		},
		[selectedCompany],
	);

	const handleSelectCompany = (company) => {
		setSelectedCompany(company);
		setCompanyInput(company.companyName);
		setShowCompanyDropdown(false);
		setSelectedContacts([]);
		setEmailOverrides({});
		setManualEmails([]);
		setDealId('');
		// Update title if it still matches the default
		setTitle((prev) => {
			if (!prev || prev === 'Meeting mit ' || prev === `Meeting mit ${resolvedCompanyName}`) {
				return `Meeting mit ${company.companyName}`;
			}
			return prev;
		});
	};

	// Close company dropdown on outside click
	useEffect(() => {
		if (!showCompanyDropdown) return;
		const handler = (e) => {
			if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) {
				setShowCompanyDropdown(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [showCompanyDropdown]);

	// Show dropdown when results arrive
	useEffect(() => {
		if (companyResults.length > 0 && !selectedCompany) setShowCompanyDropdown(true);
	}, [companyResults, selectedCompany]);

	// Mutation
	const createMutation = useMutation({
		mutationFn: (payload) => createCalendarEvent(payload),
		onMutate: async (payload) => {
			await qc.cancelQueries({ queryKey: ['crm', 'calendar', 'events'] });
			const queries = qc.getQueriesData({ queryKey: ['crm', 'calendar', 'events'] });
			const optimisticEvent = {
				id: `temp_${Date.now()}`,
				title: payload.title,
				dueAt: payload.startTime,
				durationMinutes: payload.durationMinutes,
				provider: 'optimistic',
				source: 'external',
			};
			for (const [key, data] of queries) {
				if (data?.events) {
					qc.setQueryData(key, { ...data, events: [...data.events, optimisticEvent] });
				}
			}
			return { queries };
		},
		onError: (err, _vars, context) => {
			if (context?.queries) {
				for (const [key, data] of context.queries) {
					qc.setQueryData(key, data);
				}
			}
			showError({ error: err, title: 'Meeting konnte nicht erstellt werden' });
		},
		onSettled: () => {
			qc.invalidateQueries({ queryKey: ['crm', 'calendar', 'events'] });
			if (resolvedCompanyId) qc.invalidateQueries({ queryKey: ['crm', 'activities'] });
		},
		onSuccess: () => {
			showSuccess({ title: 'Meeting geplant', autoClose: true });
			onClose();
		},
	});

	const handleSubmit = (e) => {
		e.preventDefault();
		const startTime = new Date(`${date}T${time}`).toISOString();

		// Collect emails: override > contact's own email, plus manual emails
		const contactEmails = selectedContacts
			.map((id) => {
				if (emailOverrides[id]) return emailOverrides[id];
				const c = contacts.find((ct) => ct.id === id);
				return c?.emailAddress;
			})
			.filter(Boolean);
		const attendeeEmails = [...contactEmails, ...manualEmails];

		createMutation.mutate({
			connectionId: effectiveConnectionId,
			title,
			startTime,
			durationMinutes: duration,
			attendeeEmails,
			description: description || null,
			videoCall: videoCall || null,
			...(resolvedCompanyId ? { crmCompanyId: resolvedCompanyId } : {}),
			...(dealId ? { dealId } : {}),
			...(selectedContacts[0] ? { crmContactId: selectedContacts[0] } : {}),
		});
	};

	const toggleContact = (contactId) => {
		setSelectedContacts((prev) => {
			if (prev.includes(contactId)) {
				// Deselecting — also remove any email override
				setEmailOverrides((o) => {
					const next = { ...o };
					delete next[contactId];
					return next;
				});
				return prev.filter((id) => id !== contactId);
			}
			return [...prev, contactId];
		});
	};

	const addManualEmail = () => {
		const email = manualEmailInput.trim();
		if (!email || manualEmails.includes(email)) return;
		setManualEmails((prev) => [...prev, email]);
		setManualEmailInput('');
	};

	const removeManualEmail = (email) => {
		setManualEmails((prev) => prev.filter((e) => e !== email));
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} as="form" onSubmit={handleSubmit} size="m">
			<Panel.Header icon={BsCalendar} onClose={onClose} subtitle={resolvedCompanyName}>
				Meeting planen
			</Panel.Header>
			<Panel.Body className="space-y-5">
				{connections.length === 0 ? (
					<Alert tone="caution">
						Bitte verbinden Sie zuerst einen Kalender unter CRM &rarr; Einstellungen &rarr; Integrationen.
					</Alert>
				) : (
					<>
						{showCompanyPicker && (
							<div className="relative" ref={companyDropdownRef}>
								<label className="text-xs font-medium text-text mb-1 block">Unternehmen</label>
								<div className="relative">
									<TextInput
										value={companyInput}
										onChange={handleCompanyInputChange}
										onFocus={() =>
											companyResults.length > 0 &&
											!selectedCompany &&
											setShowCompanyDropdown(true)
										}
										placeholder="Unternehmen suchen …"
										className={`pr-8 ${selectedCompany ? '!border-primary-300 bg-primary-100/50' : ''}`}
									/>
									{!selectedCompany && (
										<BsSearch className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-bgBase-800 pointer-events-none" />
									)}
									{selectedCompany && (
										<button
											type="button"
											onClick={() => {
												setSelectedCompany(null);
												setCompanyInput('');
												setSelectedContacts([]);
												setEmailOverrides({});
												setManualEmails([]);
												setDealId('');
											}}
											className="absolute right-2 top-1/2 -translate-y-1/2 text-bgBase-800 hover:text-text text-sm"
										>
											&times;
										</button>
									)}
								</div>

								{showCompanyDropdown && !selectedCompany && (
									<div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-card shadow-elevated border border-bgBase-500 max-h-48 overflow-y-auto">
										{companySearchLoading && (
											<div className="px-3 py-2 text-xs text-bgBase-800">Suche …</div>
										)}
										{!companySearchLoading &&
											companyResults.length === 0 &&
											companyInput.trim().length >= 2 && (
												<div className="px-3 py-3 text-xs text-bgBase-800 text-center">
													Kein Unternehmen gefunden
												</div>
											)}
										{companyResults.map((c) => (
											<button
												key={c.id}
												type="button"
												onClick={() => handleSelectCompany(c)}
												className="w-full text-left px-3 py-2 hover:bg-bgBase-300 transition-colors border-b border-bgBase-400 last:border-b-0"
											>
												<p className="text-sm text-text truncate">{c.companyName}</p>
											</button>
										))}
									</div>
								)}
							</div>
						)}

						<div>
							<label className="text-xs font-medium text-text mb-1 block">Titel</label>
							<TextInput value={title} onChange={(e) => setTitle(e.target.value)} required />
						</div>

						<div className="grid grid-cols-3 gap-3">
							<div>
								<label className="text-xs font-medium text-text mb-1 block">Datum</label>
								<TextInput
									type="date"
									value={date}
									onChange={(e) => setDate(e.target.value)}
									required
								/>
							</div>
							<div>
								<label className="text-xs font-medium text-text mb-1 block">Uhrzeit</label>
								<TextInput
									type="time"
									value={time}
									onChange={(e) => setTime(e.target.value)}
									required
								/>
							</div>
							<div>
								<label className="text-xs font-medium text-text mb-1 block">Dauer</label>
								<Select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
									{DURATION_OPTIONS.map((o) => (
										<option key={o.value} value={o.value}>
											{o.label}
										</option>
									))}
								</Select>
							</div>
						</div>

						{(resolvedCompanyId || manualEmails.length > 0) && (
							<div>
								<label className="text-xs font-medium text-text mb-1.5 block">Teilnehmer</label>

								{contacts.length > 0 && (
									<div className="space-y-1.5 mb-3">
										{contacts.map((c) => {
											const isSelected = selectedContacts.includes(c.id);
											const displayName =
												c.fullName || c.firstName || c.emailAddress || 'Unbekannt';
											const effectiveEmail = emailOverrides[c.id] || c.emailAddress;

											return (
												<div key={c.id}>
													<button
														type="button"
														onClick={() => toggleContact(c.id)}
														className={`w-full text-left text-xs px-3 py-2 rounded-control border transition-colors flex items-center justify-between ${
															isSelected
																? 'bg-primary-100 border-primary-300 text-primary-700'
																: 'bg-white border-bgBase-500 text-text hover:border-bgBase-600'
														}`}
													>
														<span className="font-medium">{displayName}</span>
														{effectiveEmail && (
															<span className="text-[11px] text-bgBase-800 ml-2 truncate max-w-[200px]">
																{effectiveEmail}
															</span>
														)}
													</button>

													{isSelected && (
														<TextInput
															type="email"
															value={emailOverrides[c.id] ?? c.emailAddress ?? ''}
															placeholder="E-Mail-Adresse eingeben …"
															className="mt-1 ml-4 !w-[calc(100%-1rem)] !text-[11px]"
															onChange={(e) =>
																setEmailOverrides((prev) => ({
																	...prev,
																	[c.id]: e.target.value,
																}))
															}
														/>
													)}
												</div>
											);
										})}
									</div>
								)}

								{manualEmails.length > 0 && (
									<div className="flex flex-wrap gap-1.5 mb-2">
										{manualEmails.map((email) => (
											<span
												key={email}
												className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-bgBase-400 border border-bgBase-500 text-text"
											>
												{email}
												<button
													type="button"
													onClick={() => removeManualEmail(email)}
													className="text-bgBase-800 hover:text-text"
												>
													<BsX className="w-3.5 h-3.5" />
												</button>
											</span>
										))}
									</div>
								)}

								<div className="flex gap-2">
									<TextInput
										type="email"
										placeholder="Weitere E-Mail hinzufügen …"
										className="flex-1"
										value={manualEmailInput}
										onChange={(e) => setManualEmailInput(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												e.preventDefault();
												addManualEmail();
											}
										}}
									/>
									<button
										type="button"
										onClick={addManualEmail}
										disabled={!manualEmailInput.trim()}
										className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-card border border-bgBase-500 bg-white text-bgBase-800 hover:border-bgBase-600 hover:text-text disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
									>
										<BsPlus className="w-4 h-4" />
									</button>
								</div>
							</div>
						)}

						{(hasGoogle || hasMicrosoft) && (
							<VideoCallSelector
								hasGoogle={hasGoogle}
								hasMicrosoft={hasMicrosoft}
								videoCall={videoCall}
								onChange={setVideoCall}
							/>
						)}

						{connections.length > 1 && (
							<div>
								<label className="text-xs font-medium text-text mb-1 block">Kalender</label>
								<Select
									value={effectiveConnectionId}
									onChange={(e) => setCalendarConnectionId(e.target.value)}
								>
									{connections.map((c) => (
										<option key={c.id} value={c.id}>
											{c.label || c.metadata?.calendarEmail}
										</option>
									))}
								</Select>
							</div>
						)}

						{deals.length > 0 && (
							<div>
								<label className="text-xs font-medium text-text mb-1 block">Deal (optional)</label>
								<Select value={dealId} onChange={(e) => setDealId(e.target.value)}>
									<option value="">Kein Deal</option>
									{deals.map((d) => (
										<option key={d.id} value={d.id}>
											{d.title}
										</option>
									))}
								</Select>
							</div>
						)}

						<div>
							<label className="text-xs font-medium text-text mb-1 block">Beschreibung (optional)</label>
							<Textarea
								className="min-h-[80px] !resize-none"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="Agenda, Notizen…"
							/>
						</div>
					</>
				)}
			</Panel.Body>
			{connections.length > 0 && (
				<Panel.Footer className="justify-end">
					<Button variant="outline" size="sm" type="button" onClick={onClose}>
						Abbrechen
					</Button>
					<Button
						variant="solid"
						size="sm"
						type="submit"
						loading={createMutation.isPending}
						disabled={createMutation.isPending || !effectiveConnectionId}
					>
						Meeting planen
					</Button>
				</Panel.Footer>
			)}
		</Modal>
	);
};

MeetingScheduleModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	companyId: PropTypes.string,
	companyName: PropTypes.string,
	dealId: PropTypes.string,
	prefillDate: PropTypes.string,
};

export default MeetingScheduleModal;
